import React, { ChangeEvent, useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Pagination from "@mui/lab/Pagination";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import dayjs from "../date";
import { Invoice } from "../../admin/types";
import { routePaths, setPathId } from "../../routing/route-paths";
import { backgrounds, borders } from "../../theme/theme";

const formatDateForInvoice = (dateStr: string) => {
  return dayjs(dateStr).utc().format("MMM D, YYYY");
};

const formatCurrencyAmount = (amount: number, currency: string): string => {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency || "USD",
  }).format(amount);
};

interface InvoicesListProps {
  invoices: Invoice[];
}

const InvoicesList = (props: InvoicesListProps) => {
  const { invoices } = props;

  const NUMBER_OF_INVOICES_PER_PAGE = 10;
  const [page, setPage] = useState(1);
  const [numberOfPages, setNumberOfPages] = useState(1);

  useEffect(() => {
    if (!invoices || !invoices.length) {
      setNumberOfPages(1);
      return;
    }
    setNumberOfPages(Math.ceil(invoices.length / NUMBER_OF_INVOICES_PER_PAGE));
  }, [invoices]);

  function handlePaginationChange(event: ChangeEvent<unknown>, value: number) {
    setPage(value);
  }

  const displayedInvoices = (invoices || []).slice(
    (page - 1) * NUMBER_OF_INVOICES_PER_PAGE,
    page * NUMBER_OF_INVOICES_PER_PAGE
  ) as Invoice[];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: "1px solid",
        borderColor: borders.light,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Recent Invoices
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {invoices?.length
            ? `${invoices.length} invoice${invoices.length !== 1 ? "s" : ""} total`
            : "Your invoice history"}
        </Typography>
      </Box>

      {invoices && invoices.length ? (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Invoice ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedInvoices.map((invoice) => (
                  <TableRow
                    key={invoice.publicId}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:hover": { backgroundColor: backgrounds.surface },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {invoice.publicId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDateForInvoice(invoice.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatCurrencyAmount(
                          invoice.totalPaymentAmount,
                          invoice.currency
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.paidAt ? "Paid" : "Pending"}
                        size="small"
                        color={invoice.paidAt ? "success" : "warning"}
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        component="a"
                        href={setPathId(
                          routePaths.invoices.invoicesView,
                          invoice.publicId
                        )}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ color: "primary.main" }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {numberOfPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Pagination
                count={numberOfPages}
                page={page}
                siblingCount={0}
                size="small"
                onChange={handlePaginationChange}
              />
            </Box>
          )}
        </>
      ) : (
        <Box
          sx={{
            py: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            color: "text.secondary",
          }}
        >
          <ReceiptLongIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
          <Typography>You do not have any invoices yet.</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default InvoicesList;
